import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Phone, ShieldCheck, Sparkles, Gift, Store, Bike, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import CompleteYourMeal from "../components/CompleteYourMeal";
import ClosedModal from "../components/ClosedModal";
import CloverCardForm from "../components/CloverCardForm";
import { spiceLabel } from "../components/VariantPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CITIES = ["Lethbridge", "Coaldale", "Coalhurst"];

export default function Order() {
  const { items, subtotal: itemsTotal, count, clear } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: localStorage.getItem("rivaaz_rewards_phone") || "",
    customer_email: "",
    fulfillment: "pickup",
    pickup_time: "ASAP",
    notes: "",
    payment_method: "pickup",
    // Delivery address
    d_street: "",
    d_unit: "",
    d_city: "Lethbridge",
    d_postal: "",
    d_instructions: "",
  });
  const [rewards, setRewards] = useState(null);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [zones, setZones] = useState([]);
  const [freeThreshold, setFreeThreshold] = useState(150);
  const [quote, setQuote] = useState(null); // { available, fee, eta, name, is_free, original_fee } or null while idle
  const [status, setStatus] = useState({ is_open: true }); // optimistic until /status responds
  const [cloverConfig, setCloverConfig] = useState(null);
  const [tipPct, setTipPct] = useState(0);
  const [tipCustom, setTipCustom] = useState("");
  const cloverFormRef = useRef(null);
  const lookupTimer = useRef(null);
  const quoteTimer = useRef(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Load Clover config once — decides whether to show the card form
  useEffect(() => {
    axios
      .get(`${API}/clover/config`)
      .then((r) => {
        setCloverConfig(r.data);
        // If clover is enabled, default to card payment for convenience
        if (r.data?.enabled) {
          setForm((f) => (f.payment_method === "pickup" ? { ...f, payment_method: "clover" } : f));
        }
      })
      .catch(() => setCloverConfig({ enabled: false }));
  }, []);

  // Load delivery zones once
  useEffect(() => {
    axios
      .get(`${API}/delivery/zones`)
      .then((r) => {
        // Backend response: { free_delivery_threshold, zones: [...] }
        // (Old code expected an array — handle both shapes.)
        const payload = r.data;
        if (Array.isArray(payload)) {
          setZones(payload);
        } else {
          setZones(payload?.zones || []);
          if (typeof payload?.free_delivery_threshold === "number") {
            setFreeThreshold(payload.free_delivery_threshold);
          }
        }
      })
      .catch(() => {});
  }, []);

  // Debounced rewards lookup as user types phone
  useEffect(() => {
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    const digits = form.customer_phone.replace(/\D/g, "");
    if (digits.length < 10) {
      setRewards(null);
      setRedeemPoints(0);
      return;
    }
    lookupTimer.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(
          `${API}/rewards/${encodeURIComponent(form.customer_phone)}`
        );
        setRewards(data);
        if (data.suggested_redemption) setRedeemPoints(data.suggested_redemption.points);
        else setRedeemPoints(0);
      } catch {
        setRewards(null);
      }
    }, 400);
    return () => lookupTimer.current && clearTimeout(lookupTimer.current);
  }, [form.customer_phone]);

  const discount = redeemPoints
    ? rewards?.available_rewards?.find((r) => r.points === redeemPoints)?.discount || 0
    : 0;
  const foodSubtotal = Math.max(itemsTotal - discount, 0);

  // Debounced delivery quote lookup (re-runs when subtotal changes so the
  // 'free delivery' state stays in sync as customers add/remove items).
  useEffect(() => {
    if (quoteTimer.current) clearTimeout(quoteTimer.current);
    if (form.fulfillment !== "delivery") {
      setQuote(null);
      return;
    }
    const clean = form.d_postal.replace(/\s/g, "");
    if (clean.length < 3) {
      setQuote(null);
      return;
    }
    quoteTimer.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(
          `${API}/delivery/quote?postal=${encodeURIComponent(form.d_postal)}&subtotal=${foodSubtotal.toFixed(2)}`
        );
        setQuote(data);
      } catch {
        setQuote(null);
      }
    }, 350);
    return () => quoteTimer.current && clearTimeout(quoteTimer.current);
  }, [form.d_postal, form.fulfillment, foodSubtotal]);

  const deliveryFee = form.fulfillment === "delivery" && quote?.available ? quote.fee : 0;
  const gst = Math.round(foodSubtotal * 0.05 * 100) / 100;
  // Tip: percentage of pre-tax food subtotal, or custom dollar amount.
  const tipAmount = useMemo(() => {
    if (tipCustom !== "") {
      const n = parseFloat(tipCustom);
      return isNaN(n) || n < 0 ? 0 : Math.round(n * 100) / 100;
    }
    return Math.round(foodSubtotal * (tipPct / 100) * 100) / 100;
  }, [tipPct, tipCustom, foodSubtotal]);
  const grandTotal = useMemo(
    () => Math.round((foodSubtotal + deliveryFee + gst + tipAmount) * 100) / 100,
    [foodSubtotal, deliveryFee, gst, tipAmount]
  );
  // Free-delivery progress for the bar shown in the Delivery section.
  const amountToFree = Math.max(freeThreshold - foodSubtotal, 0);
  const freeProgress = Math.min(foodSubtotal / freeThreshold, 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!status.is_open) {
      toast.error(`We're closed — opens ${status.next_open_label || "soon"}.`);
      return;
    }
    // Between 9:30 PM and 10 PM the restaurant is physically open but not
    // accepting new online orders (kitchen buffer). Block submit with a
    // friendly explainer that tells the customer to call/walk-in.
    if (status.accepting_orders === false) {
      toast.error(
        `Online orders close 30 min before we do — please call us or drop by until ${status.closes_at ? "10 PM" : "close"}.`
      );
      return;
    }
    if (items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    if (!form.customer_name.trim() || !form.customer_phone.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }
    if (form.fulfillment === "delivery") {
      if (!form.d_street.trim() || !form.d_postal.trim()) {
        toast.error("Please enter your delivery street and postal code");
        return;
      }
      if (!quote?.available) {
        toast.error("We don't deliver to that postal code yet — try pickup");
        return;
      }
    }
    setSubmitting(true);
    try {
      // For Clover online payment, tokenize the card BEFORE creating the order
      // so we never write an "online" order to MongoDB that the user then
      // bailed out of at the card form.
      let cloverSourceToken = null;
      if (form.payment_method === "clover") {
        if (!cloverFormRef.current) {
          throw new Error("Card form not ready");
        }
        const tk = await cloverFormRef.current.tokenize();
        if (tk?.error) {
          toast.error(tk.error);
          setSubmitting(false);
          return;
        }
        cloverSourceToken = tk.token;
      }

      const payload = {
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_email: form.customer_email.trim() || null,
        fulfillment: form.fulfillment,
        pickup_time: form.pickup_time,
        notes: form.notes,
        payment_method: form.payment_method,
        redeem_points: redeemPoints || null,
        tip_amount: Math.round(tipAmount * 100) / 100,
        items: items.map((i) => ({
          item_id: i.item_id,
          variant_name: i.variant_name || null,
          quantity: i.quantity,
          spice_level: i.spice_level || null,
          dip: i.dip || null,
        })),
        delivery_address:
          form.fulfillment === "delivery"
            ? {
                street: form.d_street.trim(),
                unit: form.d_unit.trim() || null,
                city: form.d_city,
                postal_code: form.d_postal.trim(),
                instructions: form.d_instructions.trim() || null,
              }
            : null,
      };
      const { data: order } = await axios.post(`${API}/orders`, payload);

      if (form.payment_method === "clover") {
        try {
          await axios.post(`${API}/orders/${order.id}/clover/pay`, {
            source: cloverSourceToken,
            email: form.customer_email.trim() || null,
          });
          clear();
          toast.success("Payment successful! Order received.");
          navigate(`/order/success?order_id=${order.id}&paid=1`);
        } catch (payErr) {
          const detail = payErr?.response?.data?.detail;
          const msg =
            (typeof detail === "object" && detail?.message) ||
            (typeof detail === "string" && detail) ||
            "Card was declined. Please try a different card.";
          toast.error(msg);
        }
      } else if (form.payment_method === "online") {
        const { data: checkout } = await axios.post(
          `${API}/orders/${order.id}/checkout`,
          { origin_url: window.location.origin }
        );
        clear();
        window.location.href = checkout.url;
      } else {
        clear();
        toast.success("Order placed! See you soon.");
        navigate(`/order/success?order_id=${order.id}&pickup=1`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Backend can return a structured detail when closed: {code, message, next_open_label}
      if (detail && typeof detail === "object" && detail.code === "restaurant_closed") {
        setStatus({ is_open: false, next_open_label: detail.next_open_label });
        toast.error(detail.message || "We're closed right now");
      } else if (detail && typeof detail === "object" && detail.code === "online_orders_closed") {
        // 9:30 PM-10 PM buffer window
        setStatus((prev) => ({ ...prev, accepting_orders: false, is_open: true, closes_at: detail.closes_at }));
        toast.error(detail.message || "Online orders are closed for tonight");
      } else {
        const msg = detail || err?.message || "Something went wrong placing your order.";
        toast.error(typeof msg === "string" ? msg : "Order failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 text-center" data-testid="order-empty">
        <ClosedModal onStatus={setStatus} />
        <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[#0E0E1A]">
          Your cart is empty
        </h1>
        <p className="text-[#57534E] mt-4">Pick a few favorites and we'll fire them up.</p>
        <Link
          to="/menu"
          data-testid="order-empty-menu-link"
          className="mt-8 inline-flex items-center gap-2 bg-[#C9853E] hover:bg-[#A6692D] text-white rounded-full px-7 py-4 font-bold"
        >
          Browse the menu
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-20" data-testid="order-page">
      <ClosedModal onStatus={setStatus} />
      <Link
        to="/menu"
        data-testid="order-back-link"
        className="inline-flex items-center gap-2 text-sm font-semibold text-[#57534E] hover:text-[#C9853E] mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Keep shopping
      </Link>
      <h1 className="font-display text-4xl md:text-6xl font-extrabold text-[#0E0E1A] leading-tight">
        Checkout
      </h1>
      <p className="text-[#57534E] mt-2">
        {count} {count === 1 ? "item" : "items"} · ${grandTotal.toFixed(2)}
      </p>

      {status.is_open && status.accepting_orders === false && (
        <div
          className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 px-5 py-4"
          data-testid="order-cutoff-banner"
        >
          <div className="text-sm font-bold text-amber-800 mb-1">
            Online orders closed for tonight
          </div>
          <div className="text-sm text-amber-900/90">
            We stop taking online orders 30 minutes before we close so the kitchen can finish everything. We're still open for pickup and walk-in until <b>10 PM</b> — please give us a call or drop by.
          </div>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10"
        data-testid="order-form"
      >
        {/* Customer + payment */}
        <div className="lg:col-span-7 space-y-8">
          {/* Fulfillment toggle */}
          <section>
            <h2 className="font-display text-xl font-bold text-[#0E0E1A] mb-4">
              How would you like it?
            </h2>
            <div className="grid grid-cols-2 gap-3" data-testid="fulfillment-options">
              <FulfillmentOption
                testId="fulfillment-pickup"
                active={form.fulfillment === "pickup"}
                onClick={() => setForm((f) => ({ ...f, fulfillment: "pickup" }))}
                Icon={Store}
                title="Pickup"
                desc="Ready in ~25 min. No fee."
              />
              <FulfillmentOption
                testId="fulfillment-delivery"
                active={form.fulfillment === "delivery"}
                onClick={() => setForm((f) => ({ ...f, fulfillment: "delivery" }))}
                Icon={Bike}
                title="Delivery"
                desc="Lethbridge, Coaldale & Coalhurst."
              />
            </div>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-[#0E0E1A] mb-4">
              {form.fulfillment === "delivery" ? "Delivery details" : "Pickup details"}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <input
                  data-testid="order-name-input"
                  type="text"
                  required
                  value={form.customer_name}
                  onChange={update("customer_name")}
                  className="w-full bg-white border border-[#E7E5E4] rounded-xl px-4 py-3 text-[#0E0E1A] focus:outline-none focus:border-[#C9853E]"
                />
              </Field>
              <Field label="Phone" required>
                <input
                  data-testid="order-phone-input"
                  type="tel"
                  required
                  value={form.customer_phone}
                  onChange={update("customer_phone")}
                  className="w-full bg-white border border-[#E7E5E4] rounded-xl px-4 py-3 text-[#0E0E1A] focus:outline-none focus:border-[#C9853E]"
                />
              </Field>
              <Field label="Email (optional)">
                <input
                  data-testid="order-email-input"
                  type="email"
                  value={form.customer_email}
                  onChange={update("customer_email")}
                  className="w-full bg-white border border-[#E7E5E4] rounded-xl px-4 py-3 text-[#0E0E1A] focus:outline-none focus:border-[#C9853E]"
                />
              </Field>
              {form.fulfillment === "pickup" && (
                <Field label="Pickup time">
                  <select
                    data-testid="order-pickup-select"
                    value={form.pickup_time}
                    onChange={update("pickup_time")}
                    className="w-full bg-white border border-[#E7E5E4] rounded-xl px-4 py-3 text-[#0E0E1A] focus:outline-none focus:border-[#C9853E]"
                  >
                    <option value="ASAP">ASAP (~25 min)</option>
                    <option value="In 30 min">In 30 min</option>
                    <option value="In 45 min">In 45 min</option>
                    <option value="In 1 hour">In 1 hour</option>
                  </select>
                </Field>
              )}
            </div>

            {form.fulfillment === "delivery" && (
              <div className="mt-5 bg-[#FAF8F5] border border-[#E7E5E4] rounded-2xl p-5" data-testid="delivery-fields">
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#C9853E] mb-3 inline-flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" /> Where should we deliver?
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Street address" required className="sm:col-span-2">
                    <input
                      data-testid="delivery-street-input"
                      type="text"
                      required={form.fulfillment === "delivery"}
                      value={form.d_street}
                      onChange={update("d_street")}
                      placeholder="123 University Dr W"
                      className="w-full bg-white border border-[#E7E5E4] rounded-xl px-4 py-3 text-[#0E0E1A] focus:outline-none focus:border-[#C9853E]"
                    />
                  </Field>
                  <Field label="Unit / apt (optional)">
                    <input
                      data-testid="delivery-unit-input"
                      type="text"
                      value={form.d_unit}
                      onChange={update("d_unit")}
                      placeholder="Apt 204"
                      className="w-full bg-white border border-[#E7E5E4] rounded-xl px-4 py-3 text-[#0E0E1A] focus:outline-none focus:border-[#C9853E]"
                    />
                  </Field>
                  <Field label="City" required>
                    <select
                      data-testid="delivery-city-select"
                      value={form.d_city}
                      onChange={update("d_city")}
                      className="w-full bg-white border border-[#E7E5E4] rounded-xl px-4 py-3 text-[#0E0E1A] focus:outline-none focus:border-[#C9853E]"
                    >
                      {CITIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Postal code" required>
                    <input
                      data-testid="delivery-postal-input"
                      type="text"
                      required={form.fulfillment === "delivery"}
                      value={form.d_postal}
                      onChange={(e) => setForm((f) => ({ ...f, d_postal: e.target.value.toUpperCase() }))}
                      placeholder="T1K 2P8"
                      className="w-full bg-white border border-[#E7E5E4] rounded-xl px-4 py-3 text-[#0E0E1A] focus:outline-none focus:border-[#C9853E] uppercase"
                    />
                  </Field>
                  <Field label="Buzzer / delivery instructions (optional)" className="sm:col-span-2">
                    <input
                      data-testid="delivery-instructions-input"
                      type="text"
                      value={form.d_instructions}
                      onChange={update("d_instructions")}
                      placeholder="Back door, buzz #302, leave at reception…"
                      className="w-full bg-white border border-[#E7E5E4] rounded-xl px-4 py-3 text-[#0E0E1A] focus:outline-none focus:border-[#C9853E]"
                    />
                  </Field>
                </div>

                {/* Free-delivery progress bar (always visible during delivery flow) */}
                <div
                  className="mt-5 rounded-2xl bg-white border border-[#E7E5E4] p-4"
                  data-testid="free-delivery-progress"
                >
                  {amountToFree > 0 ? (
                    <>
                      <div className="flex items-center justify-between text-xs font-bold tracking-wide uppercase text-[#0E0E1A]">
                        <span className="inline-flex items-center gap-1.5">
                          <Bike className="w-3.5 h-3.5 text-[#C9853E]" /> Free delivery
                        </span>
                        <span data-testid="free-delivery-remaining">
                          Add ${amountToFree.toFixed(2)} more
                        </span>
                      </div>
                      <div className="mt-2 h-2.5 rounded-full bg-[#FAF8F5] overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#C9853E] to-[#A6692D] transition-all duration-500"
                          style={{ width: `${(freeProgress * 100).toFixed(1)}%` }}
                          data-testid="free-delivery-bar"
                        />
                      </div>
                      <div className="mt-2 text-[11px] text-[#57534E]">
                        Spend <strong>${freeThreshold.toFixed(2)}</strong> in food and we waive the delivery fee.
                      </div>
                    </>
                  ) : (
                    <div
                      className="flex items-center gap-2 text-sm font-bold text-[#374F3B]"
                      data-testid="free-delivery-unlocked"
                    >
                      <Bike className="w-4 h-4" />
                      You unlocked FREE delivery — nice. 🎉
                    </div>
                  )}
                </div>

                {/* Zone feedback */}
                {form.d_postal.length >= 3 && quote && (
                  <div className="mt-4" data-testid="delivery-quote">
                    {quote.available ? (
                      <div className="flex items-center gap-2 text-sm bg-[#374F3B]/10 text-[#374F3B] rounded-xl px-4 py-3">
                        <Bike className="w-4 h-4" />
                        <div>
                          <strong>{quote.name}</strong> ·{" "}
                          {quote.is_free ? (
                            <>
                              <span className="font-bold uppercase text-[#374F3B]">FREE delivery</span>
                              {quote.original_fee ? (
                                <span className="line-through opacity-60 ml-1">
                                  ${quote.original_fee.toFixed(2)}
                                </span>
                              ) : null}
                            </>
                          ) : (
                            <>${quote.fee.toFixed(2)} delivery</>
                          )}
                          {" · "}<span className="opacity-75">{quote.eta}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2 text-sm bg-[#C9853E]/10 text-[#A6692D] rounded-xl px-4 py-3">
                        <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <div>
                          Sorry — we don't deliver to <strong>{quote.fsa}</strong> yet. Pick Pickup or call us to arrange something.
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {zones.length > 0 && !quote && (
                  <div className="mt-3 text-xs text-[#57534E]" data-testid="delivery-zones-hint">
                    We deliver to{" "}
                    {zones.map((z, i) => (
                      <span key={z.fsa}>
                        <strong>{z.city}</strong>
                        {i < zones.length - 1 ? ", " : ""}
                      </span>
                    ))}
                    .
                  </div>
                )}
              </div>
            )}

            <Field label="Order notes (optional)" className="mt-4">
              <textarea
                data-testid="order-notes-input"
                rows={3}
                value={form.notes}
                onChange={update("notes")}
                placeholder="Allergies, spice preferences, special requests…"
                className="w-full bg-white border border-[#E7E5E4] rounded-xl px-4 py-3 text-[#0E0E1A] focus:outline-none focus:border-[#C9853E]"
              />
            </Field>
          </section>

          {/* Rewards banner */}
          {rewards && rewards.points > 0 && (
            <section data-testid="rewards-banner" className="bg-gradient-to-br from-[#0E0E1A] via-[#1C1B2B] to-[#0E0E1A] rounded-3xl p-5 text-white relative overflow-hidden">
              <div className="absolute top-3 right-3 opacity-30">
                <Sparkles className="w-12 h-12 text-[#E5B07D]" />
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#E5B07D] font-bold flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Rivaaz Rewards
              </div>
              <div className="mt-1 font-display text-2xl font-extrabold">
                Hi {rewards.name?.split(" ")[0] || form.customer_name?.split(" ")[0] || "there"}! You have{" "}
                <span className="text-[#E5B07D]">{rewards.points} points</span>
              </div>

              {rewards.available_rewards.length > 0 ? (
                <div className="mt-4 space-y-2" data-testid="redemption-options">
                  {rewards.available_rewards.map((r) => {
                    const checked = redeemPoints === r.points;
                    return (
                      <label
                        key={r.points}
                        data-testid={`redeem-option-${r.points}`}
                        className={`flex items-center justify-between rounded-xl px-4 py-3 cursor-pointer transition-all border ${
                          checked
                            ? "bg-[#E5B07D] border-[#E5B07D] text-[#0E0E1A]"
                            : "bg-white/5 border-white/10 hover:border-white/30"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Gift className={`w-4 h-4 ${checked ? "text-[#0E0E1A]" : "text-[#E5B07D]"}`} />
                          <div>
                            <div className="font-bold">Apply {r.label}</div>
                            <div className={`text-xs ${checked ? "text-[#0E0E1A]/70" : "text-white/60"}`}>
                              Uses {r.points} points
                            </div>
                          </div>
                        </div>
                        <input
                          type="radio"
                          name="redeem"
                          checked={checked}
                          onChange={() => setRedeemPoints(r.points)}
                          className="sr-only"
                        />
                      </label>
                    );
                  })}
                  <button
                    type="button"
                    data-testid="redeem-skip"
                    onClick={() => setRedeemPoints(0)}
                    className={`w-full text-xs py-2 rounded-xl ${
                      redeemPoints === 0 ? "text-[#E5B07D] font-bold" : "text-white/60 hover:text-white"
                    }`}
                  >
                    Don't redeem — save points for next time
                  </button>
                </div>
              ) : (
                <p className="text-sm text-white/70 mt-2">
                  {rewards.next_tier
                    ? `${rewards.next_tier.points - rewards.points} more points until ${rewards.next_tier.label}`
                    : "Keep ordering to earn more rewards!"}
                </p>
              )}
            </section>
          )}

          <CompleteYourMeal />

          {/* Tip section */}
          <section data-testid="tip-section">
            <h2 className="font-display text-xl font-bold text-[#0E0E1A] mb-2">
              Add a tip <span className="text-[#57534E] font-normal text-sm">(optional)</span>
            </h2>
            <p className="text-sm text-[#57534E] mb-4">
              100% of your tip goes to our kitchen and delivery team.
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {[0, 10, 15, 18, 20].map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`tip-${p}`}
                  onClick={() => {
                    setTipPct(p);
                    setTipCustom("");
                  }}
                  className={`rounded-2xl px-3 py-4 border-2 transition-all text-center ${
                    tipCustom === "" && tipPct === p
                      ? "border-[#C9853E] bg-[#C9853E]/10"
                      : "border-[#E7E5E4] bg-white hover:border-[#C9853E]"
                  }`}
                >
                  <div className="font-display text-lg font-extrabold text-[#0E0E1A]">
                    {p === 0 ? "No tip" : `${p}%`}
                  </div>
                  {p > 0 && foodSubtotal > 0 && (
                    <div className="text-[11px] text-[#57534E] mt-0.5">
                      ${((foodSubtotal * p) / 100).toFixed(2)}
                    </div>
                  )}
                </button>
              ))}
              <div
                className={`rounded-2xl px-3 py-3 border-2 transition-all col-span-3 sm:col-span-1 ${
                  tipCustom !== "" ? "border-[#C9853E] bg-[#C9853E]/10" : "border-[#E7E5E4] bg-white"
                }`}
              >
                <div className="text-[11px] uppercase tracking-wider font-bold text-[#57534E] text-center mb-1">
                  Custom
                </div>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm font-bold text-[#57534E]">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={tipCustom}
                    onChange={(e) => {
                      setTipCustom(e.target.value);
                      setTipPct(-1);
                    }}
                    className="w-full rounded-lg border border-[#E7E5E4] focus:border-[#C9853E] focus:ring-2 focus:ring-[#C9853E]/20 outline-none pl-6 pr-2 py-1.5 text-sm text-right font-bold"
                    data-testid="tip-custom"
                  />
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-[#0E0E1A] mb-4">Payment</h2>
            {cloverConfig?.enabled ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="payment-options">
                  <PayOption
                    value="clover"
                    checked={form.payment_method === "clover"}
                    onChange={update("payment_method")}
                    title="Pay online with card"
                    desc="Visa, Mastercard, Amex — secured by Clover."
                    testId="pay-clover"
                  />
                  <PayOption
                    value="pickup"
                    checked={form.payment_method === "pickup"}
                    onChange={update("payment_method")}
                    title={form.fulfillment === "delivery" ? "Pay at delivery" : "Pay at pickup"}
                    desc="Cash, card, Apple Pay, contactless."
                    testId="pay-pickup"
                  />
                </div>
                {form.payment_method === "clover" && (
                  <CloverCardForm ref={cloverFormRef} config={cloverConfig} />
                )}
              </div>
            ) : (
              <div className="rounded-2xl p-5 bg-[#FAF8F5] border border-[#E7E5E4]" data-testid="payment-pickup-only">
                <div className="text-xs tracking-[0.2em] uppercase text-[#C9853E] font-bold">
                  {form.fulfillment === "delivery" ? "Pay at delivery" : "Pay at pickup"}
                </div>
                <div className="font-display text-lg font-bold text-[#0E0E1A] mt-1">
                  Cash, card, Apple Pay, contactless.
                </div>
                <p className="text-xs text-[#57534E] mt-2 leading-relaxed">
                  Online card payment will be available soon. For now, just pay when you arrive — fast and easy.
                </p>
              </div>
            )}
            <div className="mt-3 flex items-center gap-2 text-xs text-[#57534E]">
              <ShieldCheck className="w-3.5 h-3.5" />
              No card details collected on this site.
            </div>
          </section>

          <button
            type="submit"
            disabled={submitting || !status.is_open || status.accepting_orders === false}
            data-testid="order-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#C9853E] hover:bg-[#A6692D] disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-full px-7 py-4 font-bold text-base transition-all shadow-[0_10px_25px_-8px_rgba(201,133,62,0.6)]"
          >
            {!status.is_open ? (
              <>Closed · opens {status.next_open_label || "soon"}</>
            ) : status.accepting_orders === false ? (
              <>Online orders closed for tonight · call us</>
            ) : submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {form.payment_method === "clover"
                  ? "Charging card…"
                  : form.payment_method === "online"
                  ? "Redirecting to payment…"
                  : "Placing order…"}
              </>
            ) : form.payment_method === "clover" ? (
              <>Pay ${grandTotal.toFixed(2)} with card</>
            ) : form.payment_method === "online" ? (
              <>Pay ${grandTotal.toFixed(2)} with card</>
            ) : (
              <>Place order · ${grandTotal.toFixed(2)}</>
            )}
          </button>

          <a
            href="tel:+14039422565"
            data-testid="order-prefer-call"
            className="text-center w-full inline-flex justify-center items-center gap-2 text-sm text-[#57534E] hover:text-[#C9853E]"
          >
            <Phone className="w-3.5 h-3.5" /> Prefer to call? +1 (403) 942-2565
          </a>
        </div>

        {/* Order summary */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-32 glass glass-shine rounded-3xl p-6">
            <div className="font-display text-lg font-bold text-[#0E0E1A] mb-4">
              Order summary
            </div>
            <ul className="divide-y divide-[#E7E5E4]" data-testid="order-summary-items">
              {items.map((it) => (
                <li
                  key={`${it.item_id}__${it.variant_name || ""}__${it.spice_level || ""}__${it.dip || ""}`}
                  className="py-3 flex gap-3 items-start"
                >
                  <div className="flex-1">
                    <div className="text-sm font-bold text-[#0E0E1A] leading-snug">
                      {it.name}
                    </div>
                    {it.variant_label && (
                      <div className="text-xs text-[#57534E]">{it.variant_label}</div>
                    )}
                    {it.spice_level && (
                      <div className="text-[10px] text-[#C9853E] font-bold uppercase tracking-wider mt-0.5">
                        🌶 {spiceLabel(it.spice_level)}
                      </div>
                    )}
                    {it.dip && (
                      <div className="text-[10px] text-[#4A6741] font-bold uppercase tracking-wider mt-0.5">
                        🥣 {it.dip} · free
                      </div>
                    )}
                    <div className="text-xs text-[#57534E] mt-0.5">Qty {it.quantity}</div>
                  </div>
                  <div className="text-sm font-bold text-[#C9853E] whitespace-nowrap">
                    ${(it.price * it.quantity).toFixed(2)}
                  </div>
                </li>
              ))}
            </ul>
            <div className="border-t border-[#E7E5E4] mt-4 pt-4 space-y-2">
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-[#57534E]">Items</span>
                <span className="text-[#0E0E1A] font-semibold">${itemsTotal.toFixed(2)}</span>
              </div>
              {discount > 0 && (
                <div className="flex items-baseline justify-between text-sm" data-testid="summary-discount">
                  <span className="text-[#C9853E] inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Rewards discount
                  </span>
                  <span className="text-[#C9853E] font-bold">−${discount.toFixed(2)}</span>
                </div>
              )}
              {form.fulfillment === "delivery" && (
                <div className="flex items-baseline justify-between text-sm" data-testid="summary-delivery">
                  <span className="text-[#57534E] inline-flex items-center gap-1">
                    <Bike className="w-3 h-3" /> Delivery
                    {quote?.available && (
                      <span className="text-xs opacity-70">· {quote.name}</span>
                    )}
                  </span>
                  <span className="text-[#0E0E1A] font-semibold">
                    {quote?.available
                      ? quote.is_free
                        ? "FREE"
                        : `$${quote.fee.toFixed(2)}`
                      : "—"}
                  </span>
                </div>
              )}
              <div className="flex items-baseline justify-between text-sm" data-testid="summary-gst">
                <span className="text-[#57534E]">GST 5%</span>
                <span className="text-[#0E0E1A] font-semibold">${gst.toFixed(2)}</span>
              </div>
              <div className="flex items-baseline justify-between pt-2 border-t border-dashed border-[#E7E5E4]">
                <span className="text-sm text-[#57534E]">
                  {form.fulfillment === "delivery" ? "Total" : "Subtotal"}
                </span>
                <span className="font-display text-2xl font-extrabold text-[#0E0E1A]" data-testid="order-subtotal">
                  ${grandTotal.toFixed(2)}
                </span>
              </div>
              {rewards && (
                <div className="text-xs text-[#57534E] mt-1">
                  You'll earn{" "}
                  <strong className="text-[#C9853E]">+{Math.floor(foodSubtotal)} pts</strong>{" "}
                  on this order.
                </div>
              )}
            </div>
          </div>
        </aside>
      </form>
    </div>
  );
}

const Field = ({ label, required, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-xs font-semibold tracking-wide uppercase text-[#57534E] mb-1.5 block">
      {label} {required && <span className="text-[#C9853E]">*</span>}
    </span>
    {children}
  </label>
);

const PayOption = ({ value, checked, onChange, title, desc, testId }) => (
  <label
    data-testid={testId}
    className={`block cursor-pointer rounded-2xl px-5 py-4 border-2 transition-all ${
      checked ? "border-[#C9853E] bg-[#C9853E]/5" : "border-[#E7E5E4] bg-white hover:border-[#0E0E1A]"
    }`}
  >
    <input
      type="radio"
      name="payment_method"
      value={value}
      checked={checked}
      onChange={onChange}
      className="sr-only"
    />
    <div className="font-display font-bold text-[#0E0E1A]">{title}</div>
    <div className="text-xs text-[#57534E] mt-1">{desc}</div>
  </label>
);

const FulfillmentOption = ({ active, onClick, Icon, title, desc, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`text-left rounded-2xl px-5 py-4 border-2 transition-all flex items-start gap-3 ${
      active
        ? "border-[#0E0E1A] bg-[#0E0E1A] text-white shadow-md"
        : "border-[#E7E5E4] bg-white text-[#0E0E1A] hover:border-[#0E0E1A]/50"
    }`}
  >
    <div className={`rounded-full p-2 ${active ? "bg-[#C9853E] text-white" : "bg-[#FAF8F5] text-[#C9853E]"}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div>
      <div className="font-display font-bold">{title}</div>
      <div className={`text-xs mt-0.5 ${active ? "text-white/70" : "text-[#57534E]"}`}>{desc}</div>
    </div>
  </button>
);
